import { create } from "zustand";
import { authApi, type UserResponse } from "@/api/auth";
import { ApiError } from "@/api/client";
import { twoFactorApi } from "@/api/twoFactor";

interface AuthState {
  accessToken: string | null;
  user: UserResponse | null;
  loading: boolean;
  // True once the initial token-refresh-from-cookie attempt has settled. Route
  // guards must wait for this before redirecting, otherwise a hard reload on a
  // protected route bounces to /login before the session is restored.
  hydrated: boolean;
  // When login returns a 2FA challenge, the temp token lands here so the
  // 2FA page can pick it up; reset after the second factor succeeds.
  pending2FA: { tempToken: string } | null;
  hydrate(): Promise<void>;
  login(email: string, password: string): Promise<{ requires2FA: boolean }>;
  complete2FA(code: string): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<void>;
  logout(all?: boolean): Promise<void>;
  refresh(): Promise<boolean>;
  clear(): void;
}

// Single-flight refresh: the backend ROTATES and revokes the refresh token on
// every use, so concurrent 401s must share ONE /auth/refresh call -- a second
// parallel call would send the already-revoked token and log the user out.
let refreshInFlight: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  loading: false,
  hydrated: false,
  pending2FA: null,

  async hydrate() {
    if (get().accessToken) {
      set({ hydrated: true });
      return;
    }
    set({ loading: true });
    try {
      const ok = await get().refresh();
      if (ok) {
        const user = await authApi.me();
        set({ user });
      }
    } catch (err) {
      // App.tsx fire-and-forgets hydrate(), so a rejection here becomes an
      // uncaught page error (seen live as a minified ApiError when /auth/me
      // got rate-limited right after a successful refresh). Only a 401 proves
      // the session is dead; on transient failures (429, network) keep the
      // fresh access token -- the next API call retries via the 401 dance.
      if (err instanceof ApiError && err.status === 401) get().clear();
    } finally {
      set({ loading: false, hydrated: true });
    }
  },

  async login(email, password) {
    set({ loading: true });
    try {
      const res = (await authApi.login(email, password)) as unknown as
        | { access_token: string; expires_in: number; refresh_token: string }
        | { requires_2fa: true; temp_token: string };
      if ("requires_2fa" in res) {
        set({ pending2FA: { tempToken: res.temp_token } });
        return { requires2FA: true };
      }
      set({ accessToken: res.access_token, pending2FA: null });
      const user = await authApi.me();
      set({ user });
      return { requires2FA: false };
    } finally {
      set({ loading: false });
    }
  },

  async complete2FA(code) {
    const p = get().pending2FA;
    if (!p) throw new Error("no pending 2FA challenge");
    set({ loading: true });
    try {
      const res = await twoFactorApi.loginWith(p.tempToken, code);
      set({ accessToken: res.access_token, pending2FA: null });
      const user = await authApi.me();
      set({ user });
    } finally {
      set({ loading: false });
    }
  },

  async register(email, password, displayName) {
    set({ loading: true });
    try {
      const res = await authApi.register(email, password, displayName);
      set({ accessToken: res.access_token });
      const user = await authApi.me();
      set({ user });
    } finally {
      set({ loading: false });
    }
  },

  async logout(all = false) {
    try {
      await authApi.logout(all);
    } finally {
      get().clear();
    }
  },

  async refresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const res = await authApi.refresh();
        set({ accessToken: res.access_token });
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },

  clear() {
    set({ accessToken: null, user: null, pending2FA: null });
  },
}));
